import { RackMeta, DcMeta, CABLE_LIMITS } from '../../hooks/useCalculatorLogic'
import { RackPicker } from './RackPicker'
import { useNavigate } from 'react-router-dom'
import { ArrowDownUp } from 'lucide-react'

export function RackParametersPanel({
  dcs, dcCode, currentDc, racks, src, tgt, srcU, tgtU, cable, loading,
  setSrc, setTgt, setSrcU, setTgtU, setCable, handleSwap, handleCalc
}: {
  dcs: DcMeta[]
  dcCode?: string
  currentDc?: DcMeta
  racks: RackMeta[]
  src: RackMeta | null
  tgt: RackMeta | null
  srcU: number
  tgtU: number
  cable: string
  loading: boolean
  setSrc: (r: RackMeta) => void
  setTgt: (r: RackMeta) => void
  setSrcU: (u: number) => void
  setTgtU: (u: number) => void
  setCable: (c: string) => void
  handleSwap: () => void
  handleCalc: () => void
}) {
  const navigate = useNavigate()

  return (
    <div className="calculator-parameters p-3 flex flex-col gap-2.5 border-b border-border shrink-0">
      {/* DC */}
      <div>
        <label htmlFor="calculator-dc" className="block text-xs text-text-secondary mb-1">Дата-центр</label>
        <select
          id="calculator-dc"
          value={dcCode || ''}
          onChange={e => {
            navigate(`/${e.target.value}/calculator`)
          }}
          className="w-full bg-bg-input border border-border rounded px-2 py-1.5 text-xs text-text-primary outline-none"
        >
          {dcs.map(d => <option key={d.code} value={d.code}>{d.name}</option>)}
        </select>
      </div>

      {/* Source */}
      <div className="flex flex-col gap-1">
        <RackPicker label="Откуда (A)" racks={racks} value={src} onChange={setSrc} dc={currentDc} />
        {src && (
          <div className="flex items-center gap-2 mt-1">
            <label htmlFor="source-u" className="text-[11px] text-text-muted">Юнит A:</label>
            <input 
              id="source-u"
              type="number" 
              min={1} 
              max={src.u_slots || 42}
              value={srcU}
              onChange={e => setSrcU(parseInt(e.target.value) || 1)}
              className="w-16 px-2 py-1 text-xs bg-bg-input border border-border rounded text-text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
            />
          </div>
        )}
      </div>

      {/* Swap */}
      <div className="flex justify-center">
        <button
          type="button"
          onClick={handleSwap}
          disabled={!src || !tgt}
          className="flex items-center gap-1 px-2 py-0.5 text-[11px] text-text-muted border border-border rounded hover:bg-bg-hover disabled:opacity-40 transition-colors"
          title="Поменять местами"
          aria-label="Поменять стойки и юниты местами"
        >
          <ArrowDownUp size={16} />
        </button>
      </div>

      {/* Target */}
      <div className="flex flex-col gap-1">
        <RackPicker label="Куда (B)" racks={racks} value={tgt} onChange={setTgt} dc={currentDc} />
        {tgt && (
          <div className="flex items-center gap-2 mt-1">
            <label htmlFor="target-u" className="text-[11px] text-text-muted">Юнит B:</label>
            <input 
              id="target-u"
              type="number" 
              min={1} 
              max={tgt.u_slots || 42}
              value={tgtU}
              onChange={e => setTgtU(parseInt(e.target.value) || 1)}
              className="w-16 px-2 py-1 text-xs bg-bg-input border border-border rounded text-text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
            />
          </div>
        )}
      </div>

      {/* Cable type */}
      <div>
        <label htmlFor="calculator-media" className="block text-xs text-text-secondary mb-1">Тип кабеля</label>
        <select
          id="calculator-media"
          value={cable}
          onChange={e => setCable(e.target.value)}
          className="w-full bg-bg-input border border-border rounded px-2 py-1.5 text-xs text-text-primary outline-none"
        >
          {Object.entries(CABLE_LIMITS).map(([v, { label, maxM }]) => (
            <option key={v} value={v}>{label} (≤{maxM}м)</option>
          ))}
        </select>
      </div>

      {/* Calc button */}
      <button
        type="button"
        onClick={handleCalc}
        disabled={loading || !src || !tgt}
        className="w-full py-1.5 text-xs font-medium rounded bg-accent hover:bg-accent-hover text-white disabled:opacity-40 transition-colors"
      >
        {loading ? 'Расчёт...' : 'Рассчитать маршрут'}
      </button>
    </div>
  )
}
